<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Avatar from 'next/avatar/Avatar.vue';
import { useAdmin } from 'dashboard/composables/useAdmin';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import {
  useMapGetter,
  useStoreGetters,
  useStore,
} from 'dashboard/composables/store';
import ChannelName from './components/ChannelName.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import MigrateInboxModal from './components/MigrateInboxModal.vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const showDeletePopup = ref(false);
const selectedInbox = ref({});
const showMigrateModal = ref(false);

const accountId = computed(() => getters.getCurrentAccountId.value);
// Ensure reactivity by directly depending on accounts state records
const isInboxMigrationEnabled = computed(() => {
  const id = Number(accountId.value);
  const records = store.state.accounts?.records || [];
  const account = records.find(r => r.id === id) || {};
  return Boolean(account.features?.[FEATURE_FLAGS.INBOX_MIGRATION]);
});

const inboxes = useMapGetter('inboxes/getInboxes');

const inboxesList = computed(() => {
  return inboxes.value?.slice().sort((a, b) => a.name.localeCompare(b.name));
});

const uiFlags = computed(() => getters['inboxes/getUIFlags'].value);

const deleteConfirmText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.YES')} ${selectedInbox.value.name}`
);

const deleteRejectText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.NO')} ${selectedInbox.value.name}`
);

const confirmDeleteMessage = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.MESSAGE')} ${selectedInbox.value.name}?`
);
const confirmPlaceHolderText = computed(
  () =>
    `${t('INBOX_MGMT.DELETE.CONFIRM.PLACE_HOLDER', {
      inboxName: selectedInbox.value.name,
    })}`
);

const deleteInbox = async ({ id }) => {
  try {
    await store.dispatch('inboxes/delete', id);
    useAlert(t('INBOX_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.DELETE.API.ERROR_MESSAGE'));
  }
};
const closeDelete = () => {
  showDeletePopup.value = false;
  selectedInbox.value = {};
};

const confirmDeletion = () => {
  deleteInbox(selectedInbox.value);
  closeDelete();
};
const openDelete = inbox => {
  showDeletePopup.value = true;
  selectedInbox.value = inbox;
};

const CONNECTED_STATUSES = new Set([
  'connected',
  'open',
  'working',
  'online',
  'ready',
]);
const CONNECTING_STATUSES = new Set([
  'connecting',
  'starting',
  'pending',
  'scan_qr_code',
  'scan-qr-code',
  'scan qr code',
]);

const WHATSAPP_PROVIDER_I18N_MAP = {
  default: 'INBOX_MGMT.WHATSAPP_PROVIDER_LABELS.DEFAULT',
  whatsapp_cloud: 'INBOX_MGMT.WHATSAPP_PROVIDER_LABELS.WHATSAPP_CLOUD',
  baileys: 'INBOX_MGMT.WHATSAPP_PROVIDER_LABELS.BAILEYS',
  evolution: 'INBOX_MGMT.WHATSAPP_PROVIDER_LABELS.EVOLUTION',
  waha: 'INBOX_MGMT.WHATSAPP_PROVIDER_LABELS.WAHA',
  uazapi: 'INBOX_MGMT.WHATSAPP_PROVIDER_LABELS.UAZAPI',
  notificame: 'INBOX_MGMT.WHATSAPP_PROVIDER_LABELS.NOTIFICAME',
};

const isWhatsappLikeInbox = inbox => {
  if (!inbox) return false;
  if (inbox.channel_type === 'Channel::Whatsapp') return true;
  if (
    inbox.channel_type === 'Channel::TwilioSms' &&
    inbox.medium === 'whatsapp'
  )
    return true;
  return false;
};

const normalizedStatus = inbox =>
  (inbox?.provider_connection_status || '').toString().trim().toLowerCase();

const whatsappStatusContainerClass = inbox => {
  const base =
    'size-12 flex justify-center items-center rounded-full p-2 ring ring-n-solid-1 shadow-sm';
  if (!isWhatsappLikeInbox(inbox)) {
    return `${base} border border-n-strong bg-n-alpha-3`;
  }

  const status = normalizedStatus(inbox);
  if (!status) {
    return `${base} border border-n-strong bg-n-alpha-3`;
  }

  if (CONNECTED_STATUSES.has(status)) {
    return `${base} border border-green-400 bg-green-100`;
  }

  if (CONNECTING_STATUSES.has(status)) {
    return `${base} border border-yellow-400 bg-yellow-100`;
  }

  return `${base} border border-red-400 bg-red-100`;
};

const whatsappStatusIconColorClass = inbox => {
  if (!isWhatsappLikeInbox(inbox)) {
    return 'text-n-slate-10';
  }

  const status = normalizedStatus(inbox);
  if (!status) {
    return 'text-n-slate-10';
  }

  if (CONNECTED_STATUSES.has(status)) {
    return 'text-green-700';
  }

  if (CONNECTING_STATUSES.has(status)) {
    return 'text-yellow-700';
  }

  return 'text-red-700';
};

const providerDisplayName = provider => {
  const key = WHATSAPP_PROVIDER_I18N_MAP[provider];
  if (!key) {
    return '';
  }
  const label = t(key);
  return label && label !== key ? label : '';
};

const whatsappStatusTooltip = inbox => {
  if (!isWhatsappLikeInbox(inbox)) {
    return '';
  }

  const status = normalizedStatus(inbox);
  const category = (() => {
    if (!status) return 'neutral';
    if (CONNECTED_STATUSES.has(status)) return 'connected';
    if (CONNECTING_STATUSES.has(status)) return 'connecting';
    return 'disconnected';
  })();

  const statusKeyMap = {
    connected: 'INBOX_MGMT.WHATSAPP_CONNECTION_STATUS.CONNECTED',
    connecting: 'INBOX_MGMT.WHATSAPP_CONNECTION_STATUS.CONNECTING',
    disconnected: 'INBOX_MGMT.WHATSAPP_CONNECTION_STATUS.DISCONNECTED',
    neutral: 'INBOX_MGMT.WHATSAPP_CONNECTION_STATUS.UNKNOWN',
  };

  const key = statusKeyMap[category] || statusKeyMap.neutral;
  const label = t(key);
  const statusText =
    label && label !== key ? label : status || t(statusKeyMap.neutral);
  const provider = providerDisplayName(inbox.provider);

  return provider ? `${provider} • ${statusText}` : statusText;
};
</script>

<template>
  <SettingsLayout
    :no-records-found="!inboxesList.length"
    :no-records-message="$t('INBOX_MGMT.LIST.404')"
    :is-loading="uiFlags.isFetching"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('INBOX_MGMT.HEADER')"
        :description="$t('INBOX_MGMT.DESCRIPTION')"
        :link-text="$t('INBOX_MGMT.LEARN_MORE')"
        feature-name="inboxes"
      >
        <template #actions>
          <router-link v-if="isAdmin" :to="{ name: 'settings_inbox_new' }">
            <Button
              icon="i-lucide-circle-plus"
              :label="$t('SETTINGS.INBOXES.NEW_INBOX')"
            />
          </router-link>
          <Button
            v-if="isAdmin && isInboxMigrationEnabled"
            icon="i-lucide-shuffle"
            :label="$t('SETTINGS.INBOXES.MIGRATE_INBOX')"
            @click="showMigrateModal = true"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <table class="min-w-full overflow-x-auto">
        <tbody class="divide-y divide-n-weak flex-1 text-n-slate-12">
          <tr v-for="inbox in inboxesList" :key="inbox.id">
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <div class="flex items-center flex-row gap-4">
                <div
                  v-if="inbox.avatar_url"
                  class="bg-n-alpha-3 rounded-full size-12 p-2 ring ring-n-solid-1 border border-n-strong shadow-sm"
                >
                  <Avatar
                    :src="inbox.avatar_url"
                    :name="inbox.name"
                    :size="30"
                    rounded-full
                  />
                </div>
                <div
                  v-else
                  v-tooltip.top="whatsappStatusTooltip(inbox) || null"
                  :class="whatsappStatusContainerClass(inbox)"
                >
                  <ChannelIcon
                    class="size-5"
                    :class="whatsappStatusIconColorClass(inbox)"
                    :inbox="inbox"
                  />
                </div>
                <div>
                  <span class="block font-medium capitalize">
                    {{ inbox.name }}
                  </span>
                  <ChannelName
                    :channel-type="inbox.channel_type"
                    :medium="inbox.medium"
                    :provider="inbox.provider"
                    :connection-status="inbox.provider_connection_status"
                  />
                </div>
              </div>
            </td>

            <td class="py-4">
              <div class="flex gap-1 justify-end">
                <router-link
                  :to="{
                    name: 'settings_inbox_show',
                    params: { inboxId: inbox.id },
                  }"
                >
                  <Button
                    v-if="isAdmin"
                    v-tooltip.top="$t('INBOX_MGMT.SETTINGS')"
                    icon="i-lucide-settings"
                    slate
                    xs
                    faded
                  />
                </router-link>
                <Button
                  v-if="isAdmin"
                  v-tooltip.top="$t('INBOX_MGMT.DELETE.BUTTON_TEXT')"
                  icon="i-lucide-trash-2"
                  xs
                  ruby
                  faded
                  @click="openDelete(inbox)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="$t('INBOX_MGMT.DELETE.CONFIRM.TITLE')"
      :message="confirmDeleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
      :confirm-value="selectedInbox.name"
      :confirm-place-holder-text="confirmPlaceHolderText"
      @on-confirm="confirmDeletion"
      @on-close="closeDelete"
    />
    <MigrateInboxModal
      v-if="isInboxMigrationEnabled"
      v-model:show="showMigrateModal"
    />
  </SettingsLayout>
</template>
